import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { faUser, faShieldHalved } from "@fortawesome/free-solid-svg-icons";

import PageHeader from "../../components/common/PageHeader";
import Alert from "../../components/common/Alert";
import Spinner from "../../components/common/Spinner";
import Modal from "../../components/common/Modal";
import PasswordInputField from "../../components/common/PasswordInputField";
import MfaSetupWizard from "../../components/mfa/MfaSetupWizard";
import RecoveryCodesDisplay from "../../components/mfa/RecoveryCodesDisplay";
import MeService from "../../services/core/MeService";
import UserPasswordService from "../../services/core/UserPasswordService";
import UserPreferencesService from "../../services/core/UserPreferencesService";
import MfaService from "../../services/core/MfaService";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 200, 500];
const LANGUAGE_OPTIONS = [
  { value: "", labelKey: "profile.preferences.noDefaultLanguage" },
  { value: "es", labelKey: "languages.es" },
  { value: "en", labelKey: "languages.en" },
  { value: "ca", labelKey: "languages.ca" },
  { value: "eu", labelKey: "languages.eu" },
  { value: "gl", labelKey: "languages.gl" },
  { value: "pt", labelKey: "languages.pt" },
  { value: "pt-BR", labelKey: "languages.pt-BR" },
  { value: "de", labelKey: "languages.de" },
  { value: "fr", labelKey: "languages.fr" },
  { value: "it", labelKey: "languages.it" },
  { value: "jp", labelKey: "languages.jp" },
  { value: "zh", labelKey: "languages.zh" },
];

const Profile = () => {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [preferencesForm, setPreferencesForm] = useState({
    defaultDataAreaId: "",
    defaultLanguage: "",
    defaultPageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const inFlightRef = useRef(false);

  // MFA (security section)
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(false);
  const [mfaStatusError, setMfaStatusError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [reauthAction, setReauthAction] = useState(null); // null | 'disable' | 'regenerate'
  const [reauthForm, setReauthForm] = useState({ currentPassword: "", code: "" });
  const [reauthError, setReauthError] = useState(null);
  const [reauthSubmitting, setReauthSubmitting] = useState(false);
  const [regeneratedCodes, setRegeneratedCodes] = useState(null);

  const load = async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await MeService.me();
      setMe(data);
      localStorage.setItem("userData", JSON.stringify(data));
      if (data?.displayName) localStorage.setItem("displayName", data.displayName);

      const userPreferences = await UserPreferencesService.get({ force: true });
      setPreferences(userPreferences);
      setPreferencesForm({
        defaultDataAreaId: userPreferences?.defaultDataAreaId || "",
        defaultLanguage: userPreferences?.defaultLanguage || "",
        defaultPageSize: userPreferences?.defaultPageSize || 10,
      });
    } catch (e) {
      setError(e?.message || t("common.errors.generic"));
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  useEffect(() => {
    load();
    const onTenantChanged = () => load();
    const onDataAreaChanged = () => load();
    window.addEventListener("tenantChanged", onTenantChanged);
    window.addEventListener("dataAreaChanged", onDataAreaChanged);
    return () => {
      window.removeEventListener("tenantChanged", onTenantChanged);
      window.removeEventListener("dataAreaChanged", onDataAreaChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLocalProvider = me?.authProvider?.toLowerCase() === "local";

  const loadMfaStatus = async () => {
    setMfaStatusLoading(true);
    setMfaStatusError(null);
    try {
      const status = await MfaService.status();
      setMfaStatus(status);
    } catch (e) {
      setMfaStatusError(t("profile.mfa.loadError"));
    } finally {
      setMfaStatusLoading(false);
    }
  };

  useEffect(() => {
    if (isLocalProvider) {
      loadMfaStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalProvider]);

  const translateMfaError = (message) => {
    const errorKeys = {
      CURRENT_PASSWORD_INCORRECT: "profile.mfa.errors.currentPasswordIncorrect",
      INVALID_TOTP: "profile.mfa.errors.invalidTotp",
      MFA_NOT_ENABLED: "profile.mfa.errors.mfaNotEnabled",
      MFA_RATE_LIMITED: "profile.mfa.errors.rateLimited",
    };
    return t(errorKeys[message] || "profile.mfa.errors.generic");
  };

  const openReauth = (action) => {
    setReauthAction(action);
    setReauthForm({ currentPassword: "", code: "" });
    setReauthError(null);
    setRegeneratedCodes(null);
  };

  const closeReauth = () => {
    setReauthAction(null);
    setReauthForm({ currentPassword: "", code: "" });
    setReauthError(null);
    setRegeneratedCodes(null);
  };

  const submitReauth = async (event) => {
    event.preventDefault();
    setReauthError(null);

    if (!reauthForm.currentPassword || !reauthForm.code) {
      setReauthError(t("profile.mfa.reauth.fieldsRequired"));
      return;
    }

    const normalizedCode = reauthForm.code.trim();
    setReauthSubmitting(true);
    try {
      if (reauthAction === "disable") {
        await MfaService.disable(reauthForm.currentPassword, normalizedCode);
        setReauthAction(null);
        setReauthForm({ currentPassword: "", code: "" });
        setSuccess(t("profile.mfa.success.disabled"));
        await loadMfaStatus();
      } else if (reauthAction === "regenerate") {
        const result = await MfaService.regenerateRecoveryCodes(reauthForm.currentPassword, normalizedCode);
        setRegeneratedCodes(result);
        setReauthForm({ currentPassword: "", code: "" });
      }
    } catch (e) {
      setReauthError(translateMfaError(e?.message));
    } finally {
      setReauthSubmitting(false);
    }
  };

  const finishRegenerate = async () => {
    closeReauth();
    setSuccess(t("profile.mfa.success.regenerated"));
    await loadMfaStatus();
  };

  const handleWizardClose = (activated) => {
    setWizardOpen(false);
    if (activated) {
      loadMfaStatus();
    }
  };

  const formatDateTime = (isoDate) => {
    if (!isoDate) return "";
    try {
      return new Date(isoDate).toLocaleString();
    } catch (e) {
      return isoDate;
    }
  };

  const savePreferences = async () => {
    setSavingPreferences(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await UserPreferencesService.update({
        defaultDataAreaId: preferencesForm.defaultDataAreaId || "",
        defaultLanguage: preferencesForm.defaultLanguage || "",
        defaultPageSize: Number(preferencesForm.defaultPageSize) || 10,
      });
      setPreferences(updated);
      setPreferencesForm({
        defaultDataAreaId: updated?.defaultDataAreaId || "",
        defaultLanguage: updated?.defaultLanguage || "",
        defaultPageSize: updated?.defaultPageSize || 10,
      });
      setSuccess(t("profile.preferences.saved"));
    } catch (e) {
      setError(e?.message || t("common.errors.generic"));
    } finally {
      setSavingPreferences(false);
    }
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    setPasswordErrors((prev) => ({ ...prev, [name]: null }));
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  const translatePasswordError = (message) => {
    const errorKeys = {
      CURRENT_PASSWORD_INCORRECT: "changePassword.errors.currentPasswordIncorrect",
      NEW_PASSWORD_MUST_BE_DIFFERENT: "changePassword.errors.mustBeDifferent",
      PASSWORD_CHANGE_LOCAL_ONLY: "changePassword.errors.localOnly",
      PASSWORD_CREDENTIAL_NOT_FOUND: "changePassword.errors.localOnly",
      PASSWORD_FIELDS_REQUIRED: "changePassword.errors.fieldsRequired",
      PASSWORD_TOO_SHORT: "changePassword.errors.minLength",
      PASSWORD_CONFIRMATION_MISMATCH: "changePassword.errors.passwordMismatch",
    };
    return t(errorKeys[message] || "changePassword.errors.generic");
  };

  const changePassword = async (event) => {
    event.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    const nextErrors = {};
    if (!passwordForm.currentPassword) {
      nextErrors.currentPassword = t("changePassword.errors.currentPasswordRequired");
    }
    if (!passwordForm.newPassword) {
      nextErrors.newPassword = t("changePassword.errors.newPasswordRequired");
    } else if (passwordForm.newPassword.length < 6) {
      nextErrors.newPassword = t("changePassword.errors.minLength");
    }
    if (!passwordForm.confirmPassword) {
      nextErrors.confirmPassword = t("changePassword.errors.confirmPasswordRequired");
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = t("changePassword.errors.passwordMismatch");
    }

    if (Object.keys(nextErrors).length > 0) {
      setPasswordErrors(nextErrors);
      return;
    }

    setChangingPassword(true);
    try {
      await UserPasswordService.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword,
      );
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordErrors({});
      setPasswordSuccess(t("changePassword.success.changed"));
    } catch (e) {
      setPasswordError(translatePasswordError(e?.message));
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t("profile.title")}
        subtitle={t("profile.subtitle")}
        icon={faUser}
      />

      <Alert type="error" message={error} onClose={() => setError(null)} />
      <Alert type="success" message={success} onClose={() => setSuccess(null)} autoClose={3000} />

      <section className="panel">
        <div className="panel-body">
          {loading ? (
            <Spinner />
          ) : !me ? (
            <Alert type="info" message={t("profile.info.noData")} />
          ) : (
            <div className="row">
              <div className="col-md-6">
                <h4>{t("profile.sections.account")}</h4>
                <table className="table table-striped">
                  <tbody>
                    <tr>
                      <th>{t("profile.fields.subject")}</th>
                      <td>{me.subject}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.email")}</th>
                      <td>{me.email}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.displayName")}</th>
                      <td>{me.displayName}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.authProvider")}</th>
                      <td>{me.authProvider}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.superuser")}</th>
                      <td>{me.superuser ? "✓" : "✗"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="col-md-6">
                <h4>{t("profile.sections.tenant")}</h4>
                <table className="table table-striped">
                  <tbody>
                    <tr>
                      <th>{t("profile.fields.tenantId")}</th>
                      <td>{me.currentTenantId}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.role")}</th>
                      <td>{me.role}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.fields.dataAreas")}</th>
                      <td>{Array.isArray(me.dataAreas) ? me.dataAreas.join(", ") : ""}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {me && (
        <section className="panel">
          <div className="panel-body">
            <h4>{t("profile.sections.preferences")}</h4>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">{t("profile.preferences.defaultDataAreaId")}</label>
                  <select
                    className="form-control"
                    value={preferencesForm.defaultDataAreaId}
                    onChange={(event) => setPreferencesForm((prev) => ({ ...prev, defaultDataAreaId: event.target.value }))}
                  >
                    <option value="">{t("profile.preferences.noDefaultDataArea")}</option>
                    {(me.dataAreas || []).map((dataAreaId) => (
                      <option key={dataAreaId} value={dataAreaId}>{dataAreaId}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">{t("profile.preferences.defaultPageSize")}</label>
                  <select
                    className="form-control"
                    value={preferencesForm.defaultPageSize}
                    onChange={(event) => setPreferencesForm((prev) => ({ ...prev, defaultPageSize: Number(event.target.value) }))}
                  >
                    {PAGE_SIZE_OPTIONS.map((pageSize) => (
                      <option key={pageSize} value={pageSize}>{pageSize}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">{t("profile.preferences.lastDataAreaId")}</label>
                  <input
                    className="form-control"
                    value={preferences?.lastDataAreaId || ""}
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">{t("profile.preferences.defaultLanguage")}</label>
                  <select
                    className="form-control"
                    value={preferencesForm.defaultLanguage}
                    onChange={(event) => setPreferencesForm((prev) => ({ ...prev, defaultLanguage: event.target.value }))}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value || "none"} value={option.value}>{t(option.labelKey)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="control-label">{t("profile.preferences.lastLanguage")}</label>
                  <input
                    className="form-control"
                    value={preferences?.lastLanguage ? t(`languages.${preferences.lastLanguage}`) : ""}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={savePreferences}
              disabled={savingPreferences}
            >
              {savingPreferences ? `${t("common.save")}...` : t("common.save")}
            </button>
          </div>
        </section>
      )}

      {me?.authProvider?.toLowerCase() === "local" && (
        <section className="panel">
          <div className="panel-body">
            <h4>{t("changePassword.title")}</h4>
            <p className="text-muted">{t("changePassword.subtitle")}</p>
            <Alert
              type="error"
              message={passwordError}
              onClose={() => setPasswordError(null)}
            />
            <Alert
              type="success"
              message={passwordSuccess}
              onClose={() => setPasswordSuccess(null)}
              autoClose={3000}
            />

            <form className="modern-form change-password-form" onSubmit={changePassword}>
              {[
                {
                  name: "currentPassword",
                  label: "changePassword.currentPassword",
                  placeholder: "changePassword.enterCurrentPassword",
                  autoComplete: "current-password",
                },
                {
                  name: "newPassword",
                  label: "changePassword.newPassword",
                  placeholder: "changePassword.enterNewPassword",
                  autoComplete: "new-password",
                },
                {
                  name: "confirmPassword",
                  label: "changePassword.confirmPassword",
                  placeholder: "changePassword.confirmNewPassword",
                  autoComplete: "new-password",
                },
              ].map((field) => (
                <div className="form-group" key={field.name}>
                  <label htmlFor={`profile-${field.name}`}>
                    {t(field.label)} <span className="required-field">*</span>
                  </label>
                  <PasswordInputField
                    id={`profile-${field.name}`}
                    name={field.name}
                    value={passwordForm[field.name]}
                    onChange={handlePasswordChange}
                    placeholder={t(field.placeholder)}
                    className={`form-control ${passwordErrors[field.name] ? "has-error" : ""}`}
                    wrapperClassName="login-input-wrapper"
                    ariaInvalid={Boolean(passwordErrors[field.name])}
                    autoComplete={field.autoComplete}
                    disabled={changingPassword}
                    toggleLabels={{
                      show: t("common.showPassword"),
                      hide: t("common.hidePassword"),
                    }}
                  />
                  {passwordErrors[field.name] && (
                    <div className="field-error-message">{passwordErrors[field.name]}</div>
                  )}
                </div>
              ))}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={changingPassword}
              >
                {changingPassword ? t("changePassword.changing") : t("changePassword.title")}
              </button>
            </form>
          </div>
        </section>
      )}

      {isLocalProvider && (
        <section className="panel">
          <div className="panel-body">
            <h4>{t("profile.mfa.sectionTitle")}</h4>

            <Alert type="error" message={mfaStatusError} onClose={() => setMfaStatusError(null)} />

            {mfaStatusLoading ? (
              <Spinner />
            ) : mfaStatus?.enabled ? (
              <div className="mfa-status mfa-status-enabled">
                <div className="mfa-status-row">
                  <span className="mfa-status-badge mfa-status-badge-enabled">
                    <i className="fa fa-shield"></i> {t("profile.mfa.status.enabledBadge")}
                  </span>
                </div>
                <table className="table table-striped">
                  <tbody>
                    <tr>
                      <th>{t("profile.mfa.status.methodLabel")}</th>
                      <td>{t("profile.mfa.status.method")}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.mfa.status.activatedAtLabel")}</th>
                      <td>{formatDateTime(mfaStatus.activatedAt)}</td>
                    </tr>
                    <tr>
                      <th>{t("profile.mfa.status.remainingCodesLabel")}</th>
                      <td>{mfaStatus.remainingRecoveryCodes ?? 0}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mfa-actions">
                  <button type="button" className="btn btn-danger" onClick={() => openReauth("disable")}>
                    {t("profile.mfa.actions.disable")}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => openReauth("regenerate")}>
                    {t("profile.mfa.actions.regenerateCodes")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mfa-status mfa-status-disabled">
                <div className="mfa-status-row">
                  <span className="mfa-status-badge mfa-status-badge-disabled">
                    {t("profile.mfa.status.disabledBadge")}
                  </span>
                </div>
                <p className="text-muted">{t("profile.mfa.description")}</p>
                <button type="button" className="btn btn-primary" onClick={() => setWizardOpen(true)}>
                  {t("profile.mfa.actions.activate")}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <MfaSetupWizard isOpen={wizardOpen} onClose={handleWizardClose} />

      <Modal
        isOpen={!!reauthAction}
        onClose={closeReauth}
        title={t(
          reauthAction === "disable" ? "profile.mfa.reauth.disableTitle" : "profile.mfa.reauth.regenerateTitle"
        )}
        icon={faShieldHalved}
      >
        {regeneratedCodes ? (
          <RecoveryCodesDisplay
            codes={regeneratedCodes.codes}
            generatedAt={regeneratedCodes.generatedAt}
            onDone={finishRegenerate}
          />
        ) : (
          <form onSubmit={submitReauth} className="modern-form">
            <p className="text-muted">{t("profile.mfa.reauth.description")}</p>
            <Alert type="error" message={reauthError} onClose={() => setReauthError(null)} />
            <div className="form-group">
              <label htmlFor="mfa-reauth-password">{t("profile.mfa.reauth.currentPasswordLabel")}</label>
              <PasswordInputField
                id="mfa-reauth-password"
                name="currentPassword"
                value={reauthForm.currentPassword}
                onChange={(e) => setReauthForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                placeholder={t("profile.mfa.reauth.currentPasswordPlaceholder")}
                className="form-control"
                wrapperClassName="login-input-wrapper"
                autoComplete="current-password"
                disabled={reauthSubmitting}
                toggleLabels={{
                  show: t("common.showPassword"),
                  hide: t("common.hidePassword"),
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="mfa-reauth-code">{t("profile.mfa.reauth.codeLabel")}</label>
              <input
                id="mfa-reauth-code"
                name="code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="form-control"
                placeholder={t("profile.mfa.reauth.codePlaceholder")}
                value={reauthForm.code}
                onChange={(e) => setReauthForm((prev) => ({ ...prev, code: e.target.value }))}
                disabled={reauthSubmitting}
              />
            </div>
            <div className="mfa-reauth-footer">
              <button type="button" className="btn btn-secondary" onClick={closeReauth} disabled={reauthSubmitting}>
                {t("profile.mfa.actions.cancel")}
              </button>
              <button type="submit" className="btn btn-danger" disabled={reauthSubmitting}>
                {reauthSubmitting
                  ? t("profile.mfa.reauth.submitting")
                  : t(
                      reauthAction === "disable"
                        ? "profile.mfa.reauth.confirmDisable"
                        : "profile.mfa.reauth.confirmRegenerate"
                    )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default Profile;
